import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import {
  createExperimentReadToken,
  listExperimentReadTokens,
  revokeExperimentReadToken,
  getFeedPersonalizationExperiment,
  getFeedPersonalizationUser,
  updateFeedPersonalizationConfig,
} from "../../http/analytics";
import { cachedFetch } from "../../http/analyticsCache";
import { NAVY } from "./ReportParts";
import {
  CARD,
  GRID,
  INK,
  MUTED,
  VERDICTS,
  GroupLabel,
  InfoTip,
  SigLabel,
  Verdict,
  VerdictDot,
  isClear,
  variantsWithColors,
  fmtLift,
  fmtValue,
  metricDefinitions,
  signalDefinitions,
} from "./experimentParts";
import TrendsPanel from "./TrendsPanel";

const SIGNALS = [
  { key: "vote", label: "Vote" },
  { key: "like", label: "Like" },
  { key: "comment", label: "Comment" },
  { key: "commentsOpened", label: "Opened comments" },
  { key: "profileView", label: "Profile view" },
  { key: "replay", label: "Replay" },
  { key: "longWatch", label: "Long watch" },
  { key: "quickSkip", label: "Quick skip" },
  { key: "leftOnCard", label: "Left the app" },
];

const DIMENSION_LABELS = {
  category: "Category",
  format: "Format",
  rankBand: "Rank",
  ladder: "Ladder",
  entry: "Entry",
};

const fmtDuration = (rawMs) => {
  const ms = Math.max(0, rawMs);
  const h = Math.floor(ms / 3600000);
  const d = Math.floor(h / 24);
  return d
    ? `${d}d ${h % 24}h`
    : `${h}h ${Math.floor((ms % 3600000) / 60000)}m`;
};

export function useExperimentData() {
  const [state, setState] = useState({
    loading: true,
    unavailable: false,
    data: null,
  });
  // null = the latest run
  const [runId, setRunId] = useState(null);
  const load = useCallback(
    (force = false) => {
      setState((p) => ({ ...p, loading: true }));
      const params = runId ? { runId } : {};
      cachedFetch(
        "feedPersonalizationExperiment",
        getFeedPersonalizationExperiment,
        params,
        { force }
      )
        .then((r) =>
          setState({ loading: false, unavailable: false, data: r?.data || null })
        )
        .catch((e) => {
          console.error("Failed to load experiment:", e);
          setState({ loading: false, unavailable: true, data: null });
        });
    },
    [runId]
  );
  useEffect(() => {
    load();
  }, [load]);
  return {
    experiment: { ...state, runId, selectRun: setRunId },
    loadExperiment: load,
  };
}

const Labeled = ({ label, tip, children }) => (
  <Tooltip title={tip || ""}>
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "13px" }}
    >
      {label}
      {children}
    </Box>
  </Tooltip>
);

const PickOne = ({ value, options, disabled, onChange }) => (
  <Select
    size="small"
    value={value}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value)}
    sx={{ fontSize: "13px", height: 30 }}
  >
    {options.map((o) => (
      <MenuItem key={o.value} value={o.value}>
        {o.label}
      </MenuItem>
    ))}
  </Select>
);

/** Options list that always contains the current value. */
const withCurrent = (options, value, label) =>
  options.some((o) => o.value === value)
    ? options
    : [...options, { value, label: label(value) }];

// Shared geometry for the lift tracks and their axis, so ticks line up.
const TRACK_W = 260;
const TRACK_PAD = 10;
const trackX = (v, domain) =>
  TRACK_PAD + ((v + domain) / (2 * domain)) * (TRACK_W - 2 * TRACK_PAD);
const fmtTick = (v) => (v === 0 ? "0" : `${v > 0 ? "+" : "−"}${Math.abs(v)}%`);

/**
 * Forest plot row track: each group's change vs control, as a point with
 * its 95% interval, one lane per group, on a scale shared by every row.
 * Filled point = clear difference; hollow = could be chance.
 */
const LiftTrack = ({ comparisons, domain }) => {
  const lane = 14;
  const H = Math.max(26, comparisons.length * lane + 8);
  const x = (v) => trackX(Math.max(-domain, Math.min(domain, v)), domain);
  return (
    <svg
      viewBox={`0 0 ${TRACK_W} ${H}`}
      width="100%"
      style={{ display: "block", maxWidth: 420 }}
      aria-hidden="true"
    >
      <line x1={x(0)} x2={x(0)} y1={0} y2={H} stroke={GRID} strokeWidth="1.5" />
      {comparisons.map(({ variant, cmp }, i) => {
        if (cmp?.lift == null) return null;
        const y = comparisons.length === 1 ? H / 2 : 4 + lane / 2 + i * lane;
        return (
          <g key={variant.key}>
            <line
              x1={x(cmp.low)}
              x2={x(cmp.high)}
              y1={y}
              y2={y}
              stroke={variant.color}
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle
              cx={x(cmp.lift)}
              cy={y}
              r="4.5"
              fill={isClear(cmp.verdict) ? variant.color : "#fff"}
              stroke={variant.color}
              strokeWidth="2"
            />
            {/* Tag beside the interval, on whichever side has room. */}
            <SigLabel
              x={cmp.high > domain / 2 ? x(cmp.low) : x(cmp.high)}
              y={y}
              cmp={cmp}
              side={cmp.high > domain / 2 ? "left" : "right"}
              fontSize={comparisons.length > 1 ? 7 : 8}
            />
          </g>
        );
      })}
    </svg>
  );
};

const LiftAxis = ({ domain }) => (
  <svg
    viewBox={`0 0 ${TRACK_W} 14`}
    width="100%"
    style={{ display: "block", maxWidth: 420 }}
    aria-hidden="true"
  >
    {[-domain, 0, domain].map((v, i) => (
      <text
        key={v}
        x={trackX(v, domain)}
        y={11}
        fontSize="7.5"
        fill={MUTED}
        textAnchor={i === 0 ? "start" : i === 2 ? "end" : "middle"}
        dx={i === 0 ? -TRACK_PAD : i === 2 ? TRACK_PAD : 0}
      >
        {fmtTick(v)}
      </text>
    ))}
  </svg>
);

const columnsFor = (groups) => ({
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    md: `minmax(150px, 1.3fr) 80px ${groups
      .map(() => "80px")
      .join(" ")} minmax(200px, 3fr) ${groups.length > 1 ? 70 : 140}px`,
  },
  alignItems: "center",
  columnGap: 2,
});

const MetricRows = ({ title, metrics, domain, definitions, groups }) => {
  const [hover, setHover] = useState(null);
  const columns = columnsFor(groups);
  const comparisonsOf = (m) =>
    groups.map((variant) => ({
      variant,
      cmp: m.vs?.[variant.key] ?? (variant.key === "personalized" ? m.vsControl : null),
    }));
  return (
    <Paper elevation={0} sx={{ ...CARD, p: "14px 18px", mb: 2 }}>
      <Box sx={{ ...columns, pb: 1, borderBottom: `1px solid ${GRID}` }}>
        <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>
          {title}
          <InfoTip text="Per-user averages. Bar = each group vs control, 95% range. Filled = clear difference." />
        </Typography>
        <Typography sx={{ fontSize: "12px", color: MUTED, textAlign: "right" }}>
          Control
        </Typography>
        {groups.map((g) => (
          <Typography
            key={g.key}
            sx={{ fontSize: "12px", color: MUTED, textAlign: "right" }}
          >
            {groups.length > 1 ? <GroupLabel variant={g} /> : "Personalized"}
          </Typography>
        ))}
        <LiftAxis domain={domain} />
        <span />
      </Box>
      {metrics.map((m) => {
        const comparisons = comparisonsOf(m);
        return (
          <Box
            key={m.key}
            onMouseEnter={() => setHover(m.key)}
            onMouseLeave={() => setHover(null)}
            sx={{
              ...columns,
              py: 0.75,
              borderBottom: `1px solid #f2f3f5`,
              background: hover === m.key ? "#f7f8fa" : "transparent",
              position: "relative",
            }}
          >
            <Typography sx={{ fontSize: "13px", color: INK }}>
              {m.label}{" "}
              <Box component="span" sx={{ color: MUTED }}>
                {m.good === "up" ? "↑" : "↓"}
              </Box>
              <InfoTip text={definitions?.[m.key]} />
            </Typography>
            <Typography sx={{ fontSize: "13px", color: INK, textAlign: "right" }}>
              {fmtValue(m.arms.control?.mean, m.unit)}
            </Typography>
            {groups.map((g) => (
              <Typography
                key={g.key}
                sx={{ fontSize: "13px", color: INK, textAlign: "right" }}
              >
                {fmtValue(m.arms[g.key]?.mean, m.unit)}
              </Typography>
            ))}
            <LiftTrack comparisons={comparisons} domain={domain} />
            {groups.length > 1 ? (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {comparisons.map(({ variant, cmp }) => (
                  <VerdictDot key={variant.key} verdict={cmp?.verdict} title={variant.label} />
                ))}
              </Box>
            ) : (
              <Verdict verdict={comparisons[0].cmp?.verdict} />
            )}
            {hover === m.key && (
              <Box
                sx={{
                  position: "absolute",
                  right: groups.length > 1 ? 80 : 150,
                  top: "100%",
                  mt: "-4px",
                  background: "#fff",
                  border: "1px solid #e0e0e0",
                  boxShadow: "0 4px 14px rgba(20,44,92,0.12)",
                  borderRadius: "8px",
                  p: "8px 10px",
                  fontSize: "12px",
                  lineHeight: 1.55,
                  pointerEvents: "none",
                  whiteSpace: "nowrap",
                  zIndex: 2,
                }}
              >
                <Box sx={{ fontWeight: 700, color: NAVY }}>{m.label}</Box>
                {comparisons.map(({ variant, cmp }) => (
                  <Box key={variant.key} sx={{ color: INK }}>
                    {groups.length > 1 && <GroupLabel variant={variant} />}
                    {groups.length > 1 && " · "}
                    {fmtLift(cmp?.lift)} vs control (95%{" "}
                    {fmtLift(cmp?.low)} to {fmtLift(cmp?.high)})
                    {m.mde?.[variant.key] != null &&
                      ` · detects ±${m.mde[variant.key]}%`}
                  </Box>
                ))}
                <Box sx={{ color: MUTED }}>
                  {groups
                    .map((g) => `${(m.arms[g.key]?.n || 0).toLocaleString()}`)
                    .join(" / ")}{" "}
                  vs {(m.arms.control?.n || 0).toLocaleString()} users
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </Paper>
  );
};

/**
 * Decision readout: per group, only the clear results (95% range excludes
 * 0), whether the gain grows as the feed learns, and how small a change
 * the current users can detect on session length.
 */
const Readout = ({ metrics, groups, totals }) => {
  const item = (m, v) => `${m.label} ${fmtLift(v)}`;
  const line = (label, text, color) =>
    text ? (
      <Box sx={{ display: "flex", gap: 1, fontSize: "13px" }}>
        <Box component="span" sx={{ color, fontWeight: 600, minWidth: 128 }}>
          {label}
        </Box>
        <Box component="span" sx={{ color: INK }}>
          {text}
        </Box>
      </Box>
    ) : null;
  return (
    <Paper elevation={0} sx={{ ...CARD, p: "14px 18px", mb: 2 }}>
      <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "14px", mb: 1 }}>
        Readout
        <InfoTip text="Clear results only (95% range excludes 0)." />
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: `repeat(${groups.length}, 1fr)` },
          gap: 3,
        }}
      >
        {groups.map((g) => {
          const cmp = (m) => m.vs?.[g.key] ?? m.vsControl;
          const better = metrics.filter((m) => cmp(m)?.verdict === "better");
          const worse = metrics.filter((m) => cmp(m)?.verdict === "worse");
          const grows = metrics.filter((m) => m.learning?.[g.key]?.verdict === "better");
          const fades = metrics.filter((m) => m.learning?.[g.key]?.verdict === "worse");
          // Detectable change on the main metric (videos per day).
          const primary =
            metrics.find((m) => m.key === "videosPerDay") ||
            metrics.find((m) => m.key === "minutesPerSession");
          const mde = primary?.mde?.[g.key];
          const n = totals.users?.[g.key] || 0;
          const anyClear = better.length || worse.length || grows.length || fades.length;
          return (
            <Box key={g.key} sx={{ display: "grid", gap: 0.5 }}>
              <Box sx={{ fontSize: "13px", fontWeight: 600, mb: 0.25 }}>
                <GroupLabel variant={g} />
                <Box component="span" sx={{ color: MUTED, fontWeight: 400 }}>
                  {" "}
                  · {n.toLocaleString()} users
                </Box>
              </Box>
              {line("Better", better.map((m) => item(m, cmp(m).lift)).join(", "), VERDICTS.better.color)}
              {line("Worse", worse.map((m) => item(m, cmp(m).lift)).join(", "), VERDICTS.worse.color)}
              {line(
                "Grows as it learns",
                grows.map((m) => item(m, m.learning[g.key].delta)).join(", "),
                VERDICTS.better.color
              )}
              {line(
                "Fades as it learns",
                fades.map((m) => item(m, m.learning[g.key].delta)).join(", "),
                VERDICTS.worse.color
              )}
              {!anyClear && line("So far", "No clear difference", MUTED)}
              {line(
                "Can detect",
                mde != null
                  ? `±${mde}% on ${primary.label.toLowerCase()}`
                  : "Not enough data yet",
                MUTED
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

const SignalWeights = ({ weights, disabled, onSave, definitions }) => {
  const [draft, setDraft] = useState(weights);
  useEffect(() => setDraft(weights), [weights]);
  const commit = (key) => {
    const n = Number(draft[key]);
    if (!Number.isFinite(n) || n === weights[key]) {
      setDraft(weights);
      return;
    }
    onSave({ signalWeights: { [key]: n } });
  };
  return (
    <Paper
      elevation={0}
      sx={{
        ...CARD,
        p: "12px 18px",
        mb: 2,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>
        Signal weights
      </Typography>
      {SIGNALS.map((s) => (
        <Box
          key={s.key}
          sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: "13px" }}
        >
          <span>
            {s.label}
            <InfoTip text={definitions?.[s.key]} />
          </span>
          <TextField
            size="small"
            type="number"
            value={draft[s.key] ?? ""}
            disabled={disabled}
            onChange={(e) => setDraft({ ...draft, [s.key]: e.target.value })}
            onBlur={() => commit(s.key)}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            inputProps={{ step: 0.25, style: { padding: "4px 6px", width: 52 } }}
          />
        </Box>
      ))}
    </Paper>
  );
};

/** Read-only API tokens for experiment results (shown once on creation). */
const ReadAccess = () => {
  const [tokens, setTokens] = useState(null);
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    listExperimentReadTokens()
      .then((r) => setTokens(r?.data?.tokens || []))
      .catch(() => setTokens([]));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const create = async () => {
    setBusy(true);
    try {
      const r = await createExperimentReadToken({ label: "Read token" });
      setCreated(r?.data?.token || null);
      load();
    } finally {
      setBusy(false);
    }
  };
  const revoke = async (id) => {
    setBusy(true);
    try {
      await revokeExperimentReadToken(id);
      load();
    } finally {
      setBusy(false);
    }
  };
  const active = (tokens || []).filter((t) => !t.revokedAt);
  return (
    <Paper elevation={0} sx={{ ...CARD, p: "12px 18px", mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>
          API access
          <InfoTip text="Read-only tokens for experiment results. Revoke anytime." />
        </Typography>
        <Button size="small" variant="outlined" disabled={busy} onClick={create}>
          Create token
        </Button>
        {active.map((t) => (
          <Box
            key={t.id}
            sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: "12.5px", color: MUTED }}
          >
            {t.label} · {dayjs(t.createdAt).format("MMM D")}
            {t.lastUsedAt ? ` · used ${dayjs(t.lastUsedAt).format("MMM D, h:mm A")}` : " · unused"}
            <Button size="small" color="error" disabled={busy} onClick={() => revoke(t.id)}>
              Revoke
            </Button>
          </Box>
        ))}
      </Box>
      {created && (
        <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <TextField
            size="small"
            value={created}
            InputProps={{ readOnly: true }}
            onFocus={(e) => e.target.select()}
            inputProps={{ style: { fontFamily: "monospace", fontSize: "12.5px", width: 380 } }}
          />
          <Button size="small" onClick={() => navigator.clipboard?.writeText(created)}>
            Copy
          </Button>
          <Typography sx={{ fontSize: "12.5px", color: MUTED }}>Shown once</Typography>
        </Box>
      )}
    </Paper>
  );
};

const UserLookup = () => {
  const [userId, setUserId] = useState("");
  const [state, setState] = useState(null);
  const lookUp = () => {
    const id = userId.trim();
    if (!/^[0-9a-fA-F]{24}$/.test(id)) {
      setState({ error: "Enter a 24-character user id" });
      return;
    }
    setState({ loading: true });
    getFeedPersonalizationUser(id)
      .then((r) => setState({ data: r?.data }))
      .catch(() => setState({ error: "Lookup failed" }));
  };
  const d = state?.data;
  return (
    <Paper elevation={0} sx={{ ...CARD, p: "12px 18px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>
          User lookup
        </Typography>
        <TextField
          size="small"
          placeholder="User id"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && lookUp()}
          inputProps={{ style: { padding: "5px 8px", fontSize: "13px", width: 230 } }}
        />
        {state?.error && (
          <Typography sx={{ fontSize: "12.5px", color: "#d03b3b" }}>
            {state.error}
          </Typography>
        )}
        {d && (
          <Typography sx={{ fontSize: "12.5px", color: MUTED }}>
            {d.arm === "personalized"
              ? "Personalized"
              : d.arm === "control"
              ? "Control"
              : "Not enrolled"}
            {" · "}
            {d.signals.toLocaleString()} signals
            {d.updatedAt ? ` · updated ${dayjs(d.updatedAt).format("MMM D, h:mm A")}` : ""}
          </Typography>
        )}
      </Box>
      {d && d.multipliers.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
          {d.multipliers.map((m) => (
            <Box
              key={`${m.dimension}:${m.value}`}
              sx={{
                border: `1px solid ${GRID}`,
                borderRadius: "14px",
                px: 1.25,
                py: 0.25,
                fontSize: "12.5px",
                color: INK,
              }}
            >
              <Box component="span" sx={{ color: MUTED }}>
                {DIMENSION_LABELS[m.dimension] || m.dimension}
              </Box>{" "}
              {m.label}{" "}
              <b>
                {m.multiplier >= 1 ? "↑" : "↓"} {m.multiplier.toFixed(2)}×
              </b>
            </Box>
          ))}
        </Box>
      )}
      {d && d.multipliers.length === 0 && (
        <Typography sx={{ fontSize: "12.5px", color: MUTED, mt: 1 }}>
          Baseline feed
        </Typography>
      )}
    </Paper>
  );
};

export function ExperimentsPanel({ experiment, onChanged }) {
  const d = experiment.data;
  const [saving, setSaving] = useState(false);
  if (experiment.unavailable) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: "1px dashed #c9c9c9",
          borderRadius: "8px",
          p: "20px",
          color: "text.secondary",
          fontSize: "13px",
        }}
      >
        Not available on this backend yet.
      </Paper>
    );
  }
  if (!d) return null;
  const cfg = d.config;
  const runs = d.runs || [];
  const run = d.run;
  const runNumber = run ? runs.findIndex((r) => r.runId === run.runId) + 1 : 0;
  const started = run ? dayjs(run.startedAt) : null;
  const ranFor = started
    ? fmtDuration(
        (run.stoppedAt ? dayjs(run.stoppedAt).valueOf() : Date.now()) -
          started.valueOf()
      )
    : null;
  const runLabel = (r, i) =>
    `Run ${i + 1} · ${dayjs(r.startedAt).format("MMM D")} – ${
      r.stoppedAt ? dayjs(r.stoppedAt).format("MMM D") : "now"
    }`;
  const groups = variantsWithColors(d.variants);
  const sum = (o) => Object.values(o || {}).reduce((a, b) => a + (b || 0), 0);
  const users = sum(d.totals.users);
  const sessions = sum(d.totals.sessions);

  // One scale for every row and group: the widest interval, at least ±10%.
  const domain = Math.max(
    10,
    ...d.metrics.flatMap((m) =>
      Object.values(m.vs || { personalized: m.vsControl })
        .flatMap((c) => [c?.low, c?.high])
        .filter((v) => v != null)
        .map((v) => Math.min(100, Math.ceil(Math.abs(v) / 5) * 5))
    )
  );

  const save = async (patch) => {
    setSaving(true);
    try {
      await updateFeedPersonalizationConfig(patch);
      onChanged();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          ...CARD,
          p: "14px 18px",
          mb: 2,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 3,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "16px" }}>
            Personalized feed
          </Typography>
          <Typography sx={{ fontSize: "12.5px", color: MUTED }}>
            {!run
              ? "Not started"
              : `${runs.length > 1 ? `Run ${runNumber} · ` : ""}${
                  run.stoppedAt ? `Stopped · ran ${ranFor}` : `Running ${ranFor}`
                }`}
            {" · "}
            {users.toLocaleString()} users · {sessions.toLocaleString()} sessions
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2.5,
            ml: "auto",
            flexWrap: "wrap",
          }}
        >
          {runs.length > 1 && (
            <PickOne
              value={run?.runId ?? ""}
              disabled={experiment.loading}
              onChange={(v) =>
                experiment.selectRun(v === runs[runs.length - 1].runId ? null : v)
              }
              options={runs
                .map((r, i) => ({ value: r.runId, label: runLabel(r, i) }))
                .reverse()}
            />
          )}
          <Labeled
            label="Split"
            tip="Share of signed-in users enrolled, half control and half personalized"
          >
            <PickOne
              value={cfg.allocation}
              disabled={saving}
              onChange={(v) => save({ allocation: v })}
              options={withCurrent(
                [1, 0.5, 0.2].map((v) => ({ value: v, label: `${v * 100}%` })),
                cfg.allocation,
                (v) => `${Math.round(v * 100)}%`
              )}
            />
          </Labeled>
          <Labeled
            label="Groups"
            tip={
              cfg.enabled
                ? "Stop the experiment to change groups"
                : "Personalized groups for the next run"
            }
          >
            <PickOne
              value={cfg.variantPreset || "normal"}
              disabled={saving || cfg.enabled}
              onChange={(v) => save({ variantPreset: v })}
              options={[
                { value: "normal", label: "Normal" },
                { value: "category_only", label: "Category only" },
                { value: "all_vs_category", label: "All tags vs category only" },
                { value: "normal_fast", label: "Normal vs fast" },
                { value: "slow_normal_fast", label: "Slow, normal, fast" },
              ]}
            />
          </Labeled>
          <Labeled label="Cap" tip="Most a match can be boosted overall (or cut by the inverse)">
            <PickOne
              value={cfg.maxTotalMultiplier ?? 1.5}
              disabled={saving}
              onChange={(v) => save({ maxTotalMultiplier: v })}
              options={withCurrent(
                [1.25, 1.5, 2].map((v) => ({ value: v, label: `${v}×` })),
                cfg.maxTotalMultiplier ?? 1.5,
                (v) => `${v}×`
              )}
            />
          </Labeled>
          <Labeled label="Variety" tip="Share of personalized pages that show the normal feed">
            <PickOne
              value={cfg.explorationShare ?? 0.25}
              disabled={saving}
              onChange={(v) => save({ explorationShare: v })}
              options={withCurrent(
                [0, 0.1, 0.25, 0.5].map((v) => ({ value: v, label: `${v * 100}%` })),
                cfg.explorationShare ?? 0.25,
                (v) => `${Math.round(v * 100)}%`
              )}
            />
          </Labeled>
          <Labeled label="Fade" tip="Days for an unreinforced preference to fade halfway back">
            <PickOne
              value={cfg.halfLifeDays}
              disabled={saving}
              onChange={(v) => save({ halfLifeDays: v })}
              options={withCurrent(
                [7, 14, 30].map((v) => ({ value: v, label: `${v}d` })),
                cfg.halfLifeDays,
                (v) => `${v}d`
              )}
            />
          </Labeled>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            {cfg.enabled ? "On" : "Off"}
            <Switch
              checked={cfg.enabled}
              disabled={saving}
              onChange={(e) => save({ enabled: e.target.checked })}
            />
          </Box>
        </Box>
      </Paper>

      <Readout metrics={d.metrics} groups={groups} totals={d.totals} />

      <SignalWeights
        weights={cfg.signalWeights}
        disabled={saving}
        onSave={save}
        definitions={signalDefinitions(cfg)}
      />

      <TrendsPanel data={d} groups={groups} />
      <MetricRows
        title="Metrics"
        metrics={d.metrics.filter((m) => m.kind === "metric")}
        domain={domain}
        definitions={metricDefinitions(cfg)}
        groups={groups}
      />
      <MetricRows
        title="Countermetrics"
        metrics={d.metrics.filter((m) => m.kind === "counter")}
        domain={domain}
        definitions={metricDefinitions(cfg)}
        groups={groups}
      />
      <UserLookup />
      <ReadAccess />
    </Box>
  );
}
